/**
 * Types TypeScript pour les System Alerts
 */

export type AlertType = 'error' | 'warning' | 'info';
export type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';
export type DatabaseStatus = 'healthy' | 'degraded' | 'down';

export interface Alert {
  id: string;
  key: string;
  type: AlertType;
  severity: AlertSeverity;
  message: string;
  value: number;
  threshold: number;
  timestamp: number;
  resolved: boolean;
  resolvedAt?: number;
  resolvedBy?: string;
}

export interface DatabaseMetrics {
  status: DatabaseStatus;
  responseTime: string;
  activeConnections: number;
  totalConnections?: number;
}

export interface CacheMetrics {
  connected: boolean;
  totalKeys: number;
  hitRate: number;
  memoryUsage?: number;
}

export interface ServerMetrics {
  uptime: number;
  memory: {
    rss: number;
    heapTotal: number;
    heapUsed: number;
    external: number;
    arrayBuffers?: number;
  };
  cpu: {
    user: number;
    system: number;
  };
  loadAverage: number[];
  hostname: string;
  platform: string;
  nodeVersion: string;
}

export interface PerformanceMetrics {
  requestCount: number;
  errorCount: number;
  averageResponseTime: number;
  errorRate: number;
  timestamp: string;
  database: DatabaseMetrics;
  cache?: CacheMetrics;
  server: ServerMetrics;
}

// Helpers pour affichage
export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  error: 'Erreur',
  warning: 'Avertissement',
  info: 'Information',
};

export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
  critical: 'Critique',
  high: 'Haute',
  medium: 'Moyenne',
  low: 'Basse',
};

export const ALERT_SEVERITY_COLORS: Record<AlertSeverity, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  high: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  low: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
};

export const DATABASE_STATUS_COLORS: Record<DatabaseStatus, { bg: string; text: string }> = {
  healthy: { bg: 'bg-green-100', text: 'text-green-700' },
  degraded: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  down: { bg: 'bg-red-100', text: 'text-red-700' },
};
